import { getLanguages } from "@/api/languages/languages-services";
import {
  getForms,
  getQuestionsByForm,
  getConfigurableTexts,
} from "@/api/forms-questions/forms-questions-services";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Loader from "@/components/utils/loader";
import { useStore } from "@/store";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import {
  buildLegalTextOptions,
  createQuestionPayload,
  normalizeBooleanField,
} from "./question-form-utils";

export const formSchema = z
  .object({
    texts: z.record(
      z.string(),
      z.string().min(1, { message: "Este campo es requerido" }),
    ),
    descriptions: z.record(z.string(), z.string().optional()),
	    placeholders: z.record(z.string(), z.string().optional()),
	    position: z.number().min(1, { message: "Position is required" }),
	    type: z.string().min(1, { message: "Este campo es requerido" }),
	    legalTextKey: z.string().optional(),
	    // Options are edited via `useFieldArray`, which is more reliable with objects than primitives.
	    // Keep this schema tolerant (string or {text}) to avoid hard failures if legacy values exist.
	    optionsText: z
	      .record(
	        z.string(),
	        z.array(
	          z.union([
	            z.string().optional(),
	            z.object({ text: z.string().optional() }),
	          ]),
	        ),
	      )
	      .optional(),
	    active: z.boolean().optional(),
	    required: z.boolean().optional(),
	    anonymizable: z.boolean().optional(),
	    depends_id: z.preprocess((value) => {
	      if (value === "" || value == null) return null;
	      if (typeof value === "string") return Number(value);
	      if (typeof value === "object") {
	        // Defensive: some select implementations return objects like { id } or { value }.
	        if (value && typeof value.id !== "undefined") return Number(value.id);
	        if (value && typeof value.value !== "undefined") return Number(value.value);
	      }
	      return value;
	    }, z.number().nullable().optional()),
	    related_question: z.preprocess((value) => {
	      if (value === "" || value == null) return null;
	      if (typeof value === "string") return Number(value);
	      if (typeof value === "object") {
	        if (value && typeof value.id !== "undefined") return Number(value.id);
	        if (value && typeof value.value !== "undefined") return Number(value.value);
	      }
	      return value;
	    }, z.number().nullable().optional()),
	  })
  .superRefine((values, ctx) => {
    if (values.related_question && !values.depends_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Este campo es requerido",
        path: ["depends_id"],
      });
    }
  });

const CreateQuestion = ({ formType, closeModal, fetchQuestions }) => {
  const { t, i18n } = useTranslation();
  const [questionLanguages, setQuestionLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [legalTexts, setLegalTexts] = useState([]);

  // Avoid an invalid RHF field array name like `optionsText.` while language is not selected.
  const TEMP_LANGUAGE_KEY = "__temp__";
  const optionsFieldArrayLanguage = selectedLanguage || TEMP_LANGUAGE_KEY;

  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  
  const createQuestion = useStore((state) => state.createQuestion);
  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      texts: {},
      descriptions: {},
      placeholders: {},
      position: 0,
      type: "",
      legalTextKey: "Terms",
      optionsText: {},
      active: true,
	      required: false,
	      anonymizable: false,
	      depends_id: null,
      related_question: null,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `optionsText.${optionsFieldArrayLanguage}`,
  });

  const [optionsReferenceTexts, setOptionsReferenceTexts] = useState({});

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const languages = await getLanguages();
        setQuestionLanguages(languages);
      } catch (error) {
        console.error("Error fetching languages:", error);
      }
    };
    fetchLanguages();
  }, []);

  // Políticas disponibles en el tenant para asociarlas a una pregunta `terms`.
  useEffect(() => {
    const fetchLegalTexts = async () => {
      try {
        const texts = await getConfigurableTexts();
        const list = Array.isArray(texts)
          ? texts
          : Array.isArray(texts?.data)
            ? texts.data
            : [];
        setLegalTexts(list);
      } catch (error) {
        console.error("Error fetching configurable texts:", error);
      }
    };
    fetchLegalTexts();
  }, []);

  // Cargar todas las preguntas del formulario actual
  useEffect(() => {
    const fetchAllQuestions = async () => {
      try {
        const allForms = await getForms();
        const form = allForms.find((f) => f.type === formType);
        if (form?.id) {
          const questions = await getQuestionsByForm(form.id);
          setAllQuestions(questions || []);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };
    fetchAllQuestions();
  }, [formType]);

  // If the user started adding options before choosing a language, migrate them to the chosen language.
  useEffect(() => {
    if (!selectedLanguage) return;
    try {
      const tempOptions = form.getValues(`optionsText.${TEMP_LANGUAGE_KEY}`) || [];
      const targetOptions = form.getValues(`optionsText.${selectedLanguage}`) || [];

      if (Array.isArray(tempOptions) && tempOptions.length > 0 && Array.isArray(targetOptions) && targetOptions.length === 0) {
        form.setValue(`optionsText.${selectedLanguage}`, tempOptions, { shouldDirty: true });
      }

      if (Array.isArray(tempOptions) && tempOptions.length > 0) {
        form.setValue(`optionsText.${TEMP_LANGUAGE_KEY}`, [], { shouldDirty: true });
      }
    } catch (error) {
      console.error("Error migrating temp options:", error);
    }
  }, [selectedLanguage, form]);

	  useEffect(() => {
	    if (selectedLanguage) {
	      const currentOptions = form.getValues(`optionsText.${selectedLanguage}`) || [];
	      const allOptionsInForm = form.getValues("optionsText") || {};
	    }
	  }, [selectedLanguage]);

	  const coerceOptionText = (value) => {
	    if (typeof value === "string") return value;
	    if (value && typeof value === "object") return value.text || "";
	    return "";
	  };

	  const addOption = () => {
	    append({ text: "" });
	  };

  const getTextByLanguage = (texts, language) => {
    if (!texts) return "";
    
    if (Array.isArray(texts)) {
      const textObj = texts.find((text) => text.language === language);
      return textObj ? textObj.text : "";
    }
    
    if (typeof texts === 'object' && texts[language]) {
      return texts[language].text || texts[language] || "";
    }
    
    if (typeof texts === 'string') {
      return texts;
    }
    
    return "";
  };

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      if (!selectedLanguage) {
        toast.error(t("forms.privacyPoicy.selectLanguage"));
        setLoading(false);
        return;
      }
      
      const formattedData = createQuestionPayload({
        values,
        selectedLanguage,
        coerceOptionText,
      });
      
      await createQuestion(formattedData, formType);
      toast.success(t("alertDialog.toast.questionCreatedSuccess"));
      
      if (closeModal) {
        closeModal();
      }
      
      if (fetchQuestions) {
        fetchQuestions();
      }
    } catch (error) {
      console.error("Error al crear la pregunta:", error);
      toast.error(t("alertDialog.toast.questionCreatedError"));
    } finally {
      setLoading(false);
    }
  };

  const questions = allQuestions;

  const legalTextOptions = buildLegalTextOptions(
    legalTexts,
    form.watch("legalTextKey"),
    i18n.language,
  );

  return (
    <section className="w-full flex justify-center">
      <Card className="p-6 w-full text-black bg-slate-50">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.privacyPoicy.language")}</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      setSelectedLanguage(value);
                    }}
                    value={selectedLanguage}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar idioma" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {questionLanguages.map((language) => (
                        <SelectItem
                          key={language.id}
                          value={language.code.toString()}
                        >
                          {language.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedLanguage && (
              <>
                <FormField
                  control={form.control}
                  name={`texts.${selectedLanguage}`}
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>
                        {t("forms.questions.question")} (
                        {selectedLanguage.toUpperCase()})
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`descriptions.${selectedLanguage}`}
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>
                        {t("forms.questions.description")}(
                        {selectedLanguage.toUpperCase()})
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`placeholders.${selectedLanguage}`}
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>
                        {t("forms.questions.placeholder")} (
                        {selectedLanguage.toUpperCase()})
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0 mb-4">
                  <FormControl>
                    <Checkbox
                      checked={normalizeBooleanField(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(normalizeBooleanField(checked))
                      }
                      id="active"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="active"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {t("forms.questions.activate")}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="required"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0 mb-4">
                  <FormControl>
                    <Checkbox
                      checked={normalizeBooleanField(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(normalizeBooleanField(checked))
                      }
                      id="required"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="required"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {t("forms.questions.required")}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="anonymizable"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0 mb-4">
                  <FormControl>
                    <Checkbox
                      checked={normalizeBooleanField(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(normalizeBooleanField(checked))
                      }
                      id="anonymizable"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="anonymizable"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {t("forms.questions.anonymizable")}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.questions.position")}</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value ? field.value.toString() : ""}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="1">
                        {t("forms.questions.screen")} 1
                      </SelectItem>
                      <SelectItem value="2">
                        {t("forms.questions.screen")} 2
                      </SelectItem>
                      <SelectItem value="3">
                        {t("forms.questions.screen")} 3
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.questions.questionType")}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona tipo de pregunta" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="select">Select</SelectItem>
                      <SelectItem value="radio">Radio</SelectItem>
                      <SelectItem value="input">Input</SelectItem>
                      <SelectItem value="textarea">TextArea</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="terms">Terminos</SelectItem>
                      <SelectItem value="file">Fichero</SelectItem>
                      <SelectItem value="password">Contraseña</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch("type") === "terms" && (
              <FormField
                control={form.control}
                name="legalTextKey"
                render={({ field }) => (
                  <FormItem className="mb-4">
                    <FormLabel>
                      {t("forms.questions.legalTextKey")}
                    </FormLabel>
                    <Select
                      value={field.value || "Terms"}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Terms" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {legalTextOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {t("forms.questions.legalTextKeyHelp")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {(form.watch("type") === "select" ||
              form.watch("type") === "radio") && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mt-2">
                  <FormLabel>{t("forms.questions.options")}</FormLabel>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addOption}
                  >
                    {t("forms.questions.addOption")}
                  </Button>
                </div>

	                {fields.map((field, index) => {
	                  const allOptionsInForm = form.getValues("optionsText") || {};
	                  let placeholderText = "";
	                  
	                  if (allOptionsInForm.es && allOptionsInForm.es[index]) {
	                    placeholderText = `ES: ${coerceOptionText(allOptionsInForm.es[index])}`;
	                  }
	                  else if (allOptionsInForm.en && allOptionsInForm.en[index]) {
	                    placeholderText = `EN: ${coerceOptionText(allOptionsInForm.en[index])}`;
	                  }
	                  else {
	                    placeholderText = `${t("forms.questions.option")} ${index + 1}`;
	                  }
                  
                  return (
                    <div key={field.id} className="flex space-x-2">
	                      <FormField
	                        control={form.control}
	                        name={`optionsText.${optionsFieldArrayLanguage}.${index}.text`}
	                        render={({ field }) => (
	                          <FormItem className="flex-1 mb-4">
	                            <FormControl>
	                              <Input
                                {...field}
                                placeholder={placeholderText}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => remove(index)}
                      >
                        {t("forms.questions.remove")}
                      </Button>
                    </div>
                  );
                })}

                <FormField
                  control={form.control}
                  name="related_question"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>
                        {t("forms.questions.relatedQuestion")}
                      </FormLabel>
                      <Select
                        onValueChange={(value) => {
                          if (value === "__none__") {
                            field.onChange(null);
                            setSelectedQuestion(null);
                            form.setValue("depends_id", null);
                            return;
                          }
                          const selectedQuestion = questions.find(
                            (question) => question.id === Number(value),
                          );
                          field.onChange(Number(value));
                          setSelectedQuestion(selectedQuestion);
                        }}
                        value={field.value ? field.value.toString() : ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona la pregunta relacionada" />
                          </SelectTrigger>
                        </FormControl>
                        {(() => {
                          const candidates = (questions || [])
                            .map((q) => ({
                              id: q?.id,
                              type: String(q?.type || "").toLowerCase(),
                              texts: q?.texts,
                              alias: q?.alias,
                            }))
                            .filter((q) => q.type === "radio" || q.type === "select");
                          return (
                            <SelectContent>
                              <SelectItem value="__none__">— None —</SelectItem>
                              {candidates.map((question) => (
                                <SelectItem key={question.id} value={question.id.toString()}>
                                  {getTextByLanguage(
                                    question.texts,
                                    selectedLanguage,
                                  ) || question.alias}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          );
                        })()}
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {selectedQuestion && Array.isArray(selectedQuestion.options) && selectedQuestion.options.length > 0 && (
                  <FormField
                    control={form.control}
                    name="depends_id"
                    render={({ field }) => (
                      <FormItem className="mb-4">
                        <FormLabel>
                          {t("forms.questions.optionRelated")}
                        </FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(Number(value));
                          }}
                          value={field.value ? field.value.toString() : ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona la opción relacionada" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {selectedQuestion.options.map((option) => (
                              <SelectItem key={option.id} value={option.id.toString()}>
                                {getTextByLanguage(
                                  option.text,
                                  selectedLanguage,
                                ) || "no tiene opciones"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}

            <Button
              type="submit"
              className="mt-4 bg-primary hover:bg-primary-600"
            >
              {loading ? (
                <Loader style={"buttonLoader"} />
              ) : (
                t("buttonTexts.create")
              )}
            </Button>
          </form>
        </Form>
      </Card>
    </section>
  );
};

export default CreateQuestion;
